import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, Menu, Search, ShoppingBag, User } from "lucide-react";
import { brand, mainNav } from "@/config/brand";
import { MobileNav } from "@/components/layout/MobileNav";
import { useUI } from "@/context/UIContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";

function IconButton({ children, onClick, label, testId, badgeTestId, badge, className = "" }) {
  return (
    <button onClick={onClick} aria-label={label} data-testid={testId} className={`relative grid h-10 w-10 place-items-center text-ink transition-colors hover:text-gold ${className}`}>
      {children}
      {badge > 0 && (
        <span data-testid={badgeTestId} className="absolute right-0.5 top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-gold px-1 text-[9px] font-semibold text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function Logo({ className = "" }) {
  return (
    <Link to="/" data-testid="brand-logo" className={`font-serif text-2xl font-light tracking-[0.18em] text-ink transition-opacity hover:opacity-80 ${className}`}>
      {brand.name}
    </Link>
  );
}

function DesktopNav() {
  return (
    <nav className="hidden items-center gap-7 lg:flex">
      {mainNav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          data-testid={item.testId}
          className={({ isActive }) => `text-[11px] font-medium uppercase tracking-[0.16em] transition-colors ${isActive ? "text-gold" : "text-ink hover:text-gold"}`}
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function useScrolled(offset = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > offset);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [offset]);
  return scrolled;
}

export function Header() {
  const navigate = useNavigate();
  const { setCartOpen, setSearchOpen, mobileNavOpen, setMobileNavOpen } = useUI();
  const { count } = useCart();
  const { count: wishCount } = useWishlist();
  const { isAuthenticated } = useAuth();
  const scrolled = useScrolled();

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-colors duration-300 ${scrolled ? "border-border bg-ivory/95 backdrop-blur-md" : "border-transparent bg-ivory/80 backdrop-blur-sm"}`}
      data-testid="sticky-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between lg:h-20">
          <div className="flex items-center gap-2">
            <IconButton label="Open menu" testId="mobile-menu-trigger" onClick={() => setMobileNavOpen(true)} className="lg:hidden">
              <Menu size={22} />
            </IconButton>
            <Logo className="hidden lg:block" />
          </div>

          <DesktopNav />
          <Logo className="absolute left-1/2 -translate-x-1/2 lg:hidden" />

          <div className="flex items-center gap-0.5">
            <IconButton label="Search" testId="search-modal-trigger" onClick={() => setSearchOpen(true)}>
              <Search size={20} />
            </IconButton>
            <IconButton label="Account" testId="account-link" onClick={() => navigate(isAuthenticated ? "/account" : "/login")} className="hidden lg:grid">
              <User size={20} />
            </IconButton>
            <IconButton label="Wishlist" testId="wishlist-trigger" badgeTestId="wishlist-count-badge" onClick={() => navigate("/wishlist")} badge={wishCount} className="hidden lg:grid">
              <Heart size={20} />
            </IconButton>
            <IconButton label="Cart" testId="cart-drawer-trigger" badgeTestId="cart-count-badge" onClick={() => setCartOpen(true)} badge={count}>
              <ShoppingBag size={20} />
            </IconButton>
          </div>
        </div>
      </div>

      <MobileNav open={mobileNavOpen} onOpenChange={setMobileNavOpen} isAuthenticated={isAuthenticated} wishCount={wishCount} />
    </header>
  );
}
